import React, { useState, useEffect } from 'react';
import { FaSignOutAlt, FaArrowLeft, FaSignInAlt, FaUserCircle, FaExclamationTriangle, FaTimes, FaUser, FaSave, FaEdit } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import BuzzTicketLogo from '../assets/BuzzTicketLogo.png';
import './css/GlobalHeader.css';

const GlobalHeader = ({
  title,
  subtitle,
  backTo = '/mainmenu',
  logoutTo = '/',
  logoSrc = BuzzTicketLogo,
  onBack,
  onLogout,
  onLogin,
  renderCenter,
  hideBackButton = false,
  hideLogoutButton = false,
  showLoginButton = false,
  loginTo = '/',
  headerStyle,
  hideLogo = false,
  userName,
  showProfile = false,
  profileIncomplete = false,
}) => {
  const nav = useNavigate();
  const location = useLocation();
  const [logoFailed, setLogoFailed] = useState(false);
  
  // Check if current route is an admin route
  const isAdminRoute = location.pathname.startsWith('/admin') || 
                       location.pathname.startsWith('/adminvieweventrequest') ||
                       location.pathname === '/event-approval' ||
                       location.pathname === '/events-history' ||
                       location.pathname === '/users' ||
                       location.pathname === '/viewingcustomer' ||
                       location.pathname === '/viewingorganiser' ||
                       location.pathname === '/user-growth-analytics' ||
                       location.pathname === '/event-performance' ||
                       location.pathname === '/geographic-events' ||
                       location.pathname === '/ticket-sales' ||
                       location.pathname.startsWith('/viewingcustomer') ||
                       location.pathname.startsWith('/viewingorganiser');
  const [user, setUser] = useState(null);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [profileData, setProfileData] = useState({
    firstname: '',
    surname: '',
    cellnumber: '',
    company: '',
    vat_no: '',
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    email: '',
    role: '',
    hasCustomerAccess: false,
    hasOrganizerAccess: false
  });
  const [updateMessage, setUpdateMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isProfileComplete, setIsProfileComplete] = useState(false);

  // Banking edit state
  const [isEditingBanking, setIsEditingBanking] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [bankingForm, setBankingForm] = useState({ bank_name: '', bank_code: '', bank_account_name: '', bank_account_number: '' });
  const [bankingErrors, setBankingErrors] = useState({});
  const [bankingMessage, setBankingMessage] = useState('');
  const [bankingLoading, setBankingLoading] = useState(false);

  // Validation functions
  const validateName = (name) => {
    // Allow letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    return nameRegex.test(name.trim());
  };

  const validateCellNumber = (cellnumber) => {
    // Remove any non-digit characters first
    const cleanNumber = cellnumber.replace(/\D/g, '');
    // Must start with 0 and be exactly 10 digits
    const isValidFormat = cleanNumber.length === 10 && cleanNumber.startsWith('0');
    
    // Block common test/placeholder numbers
    const blockedNumbers = ['0000000000', '0123456789'];
    const isBlocked = blockedNumbers.includes(cleanNumber);
    
    return isValidFormat && !isBlocked;
  };

  const validateForm = () => {
    const errors = {};
    
    if (!profileData.firstname.trim()) {
      errors.firstname = 'First name is required';
    } else if (!validateName(profileData.firstname)) {
      errors.firstname = 'First name can only contain letters, spaces, hyphens, and apostrophes';
    }

    if (!profileData.surname.trim()) {
      errors.surname = 'Surname is required';
    } else if (!validateName(profileData.surname)) {
      errors.surname = 'Surname can only contain letters, spaces, hyphens, and apostrophes';
    }

    if (!profileData.cellnumber.trim()) {
      errors.cellnumber = 'Cell number is required';
    } else if (!validateCellNumber(profileData.cellnumber)) {
      errors.cellnumber = 'Cell number must start with 0, be exactly 10 digits';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Auto-fetch user information on component mount
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Fetch user profile from server to get complete info including name
          const response = await fetch(`${API_BASE_URL}/shared/user-profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            if (userData.success && userData.data) {
              setUser({
                email: userData.data.email,
                userId: userData.data.user_id,
                role: userData.data.role,
                name: userData.data.name
              });
            } else {
              // Fallback to JWT token decoding
              const payload = JSON.parse(atob(token.split('.')[1]));
              setUser({
                email: payload.email,
                userId: payload.userId,
                role: payload.role
              });
            }
          } else {
            // Fallback to JWT token decoding
            const payload = JSON.parse(atob(token.split('.')[1]));
            setUser({
              email: payload.email,
              userId: payload.userId,
              role: payload.role
            });
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          // Fallback to JWT token decoding
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            setUser({
              email: payload.email,
              userId: payload.userId,
              role: payload.role
            });
          } catch (jwtError) {
            console.error('Error decoding token:', jwtError);
          }
        }
      }
    };
    fetchUser();
  }, []);

  // Handle profile popup
  const handleProfileClick = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      // Fetch basic user profile
      const userResponse = await fetch(`${API_BASE_URL}/shared/user-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Fetch organizer profile to check access
      const organizerResponse = await fetch(`${API_BASE_URL}/shared/check-organizer-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      let userData = {};
      let organizerData = {};
      let hasCustomerAccess = false;
      let hasOrganizerAccess = false;

      if (userResponse.ok) {
        const userResult = await userResponse.json();
        if (userResult.success && userResult.data) {
          userData = userResult.data;
          hasCustomerAccess = true;
        }
      }

      if (organizerResponse.ok) {
        const organizerResult = await organizerResponse.json();
        console.log('🔍 Organizer profile result:', organizerResult);
        if (organizerResult.profile) {
          organizerData = organizerResult.profile;
          hasOrganizerAccess = organizerResult.canAccessOrganizer || false;
          console.log('📊 Organizer data:', organizerData);
          console.log('🔐 Has organizer access:', hasOrganizerAccess);
        }
      } else {
        console.log('❌ Organizer access check failed:', organizerResponse.status);
      }

      // Combine all profile data
      const finalProfileData = {
        firstname: userData.firstname || '',
        surname: userData.surname || '',
        cellnumber: userData.cellnumber || '',
        company: organizerData.company || '',
        vat_no: organizerData.vat_no || '',
        bank_name: organizerData.bank_name || '',
        bank_account_name: organizerData.bank_account_name || '',
        bank_account_number: organizerData.bank_account_number || '',
        email: userData.email || '',
        role: userData.role || '',
        hasCustomerAccess,
        hasOrganizerAccess
      };
      
      console.log('🎯 Final profile data:', finalProfileData);
      setProfileData(finalProfileData);
      
      // Check if basic profile is complete
      const complete = userData.firstname && userData.surname && 
                      userData.cellnumber && 
                      userData.cellnumber !== '0000000000' && 
                      userData.cellnumber !== '0123456789';
      setIsProfileComplete(complete);
    } catch (error) {
      console.error('Error fetching profile data:', error);
    }
    
    setShowProfilePopup(true);
  };

  // Handle profile update
  const handleProfileUpdate = async () => {
    // Validate form first
    if (!validateForm()) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    setIsUpdating(true);
    setUpdateMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/shared/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      const result = await response.json();

      if (result.success) {
        setUpdateMessage('Profile updated successfully!');
        // Refresh user data
        setTimeout(() => {
          setShowProfilePopup(false);
          window.location.reload();
        }, 1500);
      } else {
        setUpdateMessage(result.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setUpdateMessage('Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const startEditBanking = async () => {
    setBankingForm({
      bank_name: profileData.bank_name || '',
      bank_code: '',
      bank_account_name: profileData.bank_account_name || '',
      bank_account_number: '',
    });
    setBankingErrors({});
    setBankingMessage('');
    setIsEditingBanking(true);

    if (bankList.length === 0) {
      try {
        const res = await fetch(`${API_BASE_URL}/payments/banks`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const data = await res.json();
        if (data.success && data.banks) setBankList(data.banks);
      } catch {
        // bank list unavailable — user can still type
      }
    }
  };

  const handleBankSelect = (e) => {
    const selected = bankList.find(b => b.name === e.target.value);
    setBankingForm(f => ({ ...f, bank_name: selected?.name || '', bank_code: selected?.code || '' }));
    setBankingErrors(prev => ({ ...prev, bank_name: '', bank_code: '' }));
  };

  const validateBankingForm = () => {
    const errs = {};
    if (!bankingForm.bank_name) errs.bank_name = 'Please select a bank.';
    if (!bankingForm.bank_code) errs.bank_code = 'Bank code is missing. Re-select your bank.';
    if (!bankingForm.bank_account_name.trim()) errs.bank_account_name = 'Account name is required.';
    const acct = bankingForm.bank_account_number.replace(/\s/g, '');
    if (!acct) errs.bank_account_number = 'Account number is required.';
    else if (!/^\d+$/.test(acct)) errs.bank_account_number = 'Account number must contain only digits.';
    else if (acct.length < 6 || acct.length > 20) errs.bank_account_number = 'Account number must be 6–20 digits.';
    setBankingErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveBanking = async () => {
    if (!validateBankingForm()) return;
    setBankingLoading(true);
    setBankingMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/organiser/update-banking-details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          bank_name: bankingForm.bank_name,
          bank_code: bankingForm.bank_code,
          bank_account_name: bankingForm.bank_account_name.trim(),
          bank_account_number: bankingForm.bank_account_number.replace(/\s/g, ''),
        }),
      });
      const data = await res.json();

      if (data.success) {
        setBankingMessage('Banking details updated successfully!');
        setProfileData(prev => ({
          ...prev,
          bank_name: bankingForm.bank_name,
          bank_account_name: bankingForm.bank_account_name.trim(),
          bank_account_number: bankingForm.bank_account_number.replace(/\s/g, ''),
        }));
        setTimeout(() => { setIsEditingBanking(false); setBankingMessage(''); }, 1800);
        return;
      }

      // Map backend error codes to specific field errors — mirrors handleOrganizerUpdate
      const fieldErrors = {};
      if (res.status === 409 && data.error === 'Bank account already in use') {
        fieldErrors.bank_account_number = 'This account number is already registered to another organizer.';
      } else if (res.status === 400 && data.error === 'Invalid account number') {
        fieldErrors.bank_account_number = data.details || 'Account number must be 6–20 digits.';
      } else if (res.status === 400 && data.error === 'Invalid bank code') {
        fieldErrors.bank_code = data.details || 'Bank code is invalid. Please re-select your bank.';
      } else if (res.status === 400 && data.error === 'Missing required fields') {
        fieldErrors._general = data.details || 'All banking fields are required.';
      } else if (res.status === 400 && (data.error === 'Paystack update failed' || data.error === 'Paystack subaccount creation failed')) {
        fieldErrors._general = data.details || 'Could not verify your bank details with Paystack. Please check them and try again.';
      } else if (res.status === 429) {
        fieldErrors._general = data.details || 'Too many attempts. Please wait a moment and try again.';
      } else {
        fieldErrors._general = data.details || data.error || 'Failed to update banking details.';
      }

      if (fieldErrors._general) {
        setBankingMessage(fieldErrors._general);
      }
      setBankingErrors(prev => ({ ...prev, ...fieldErrors }));
    } catch {
      setBankingMessage('Network error. Please try again.');
    } finally {
      setBankingLoading(false);
    }
  };

  return (
    <>
    <header className="global-header" style={headerStyle}>
      <div className="global-header__content">
        {/* Left Section - Back Button + Logo */}
        <div className="global-header__left">
          {!hideBackButton && (
            <div className="global-header__back-slot">
              <button
                className="global-header__btn"
                onClick={() => {
                  if (onBack) {
                    onBack();
                  } else {
                    nav(backTo);
                  }
                }}
              >
                <FaArrowLeft />
                <span>Back</span>
              </button>
            </div>
          )}
          {!hideLogo && (
            <img
              src={logoFailed ? '/fallback-logo.png' : logoSrc}
              alt="XPress Tickets Logo"
              className="global-header__logo"
              onError={() => setLogoFailed(true)}
            />
          )}
        </div>

        {/* Right Section - Profile and Logout */}
        <div className="global-header__right">
          {user && !isAdminRoute && (
            <div 
              className={`global-header__profile ${profileIncomplete ? 'global-header__profile--incomplete' : ''}`}
              onClick={handleProfileClick}
              style={{ cursor: 'pointer' }}
            >
              <FaUserCircle className="global-header__profile-icon" />
              <span className="global-header__profile-name">{user?.name || user?.email || ''}</span>
              {profileIncomplete && (
                <FaExclamationTriangle className="global-header__profile-warning" />
              )}
            </div>
          )}
          {!hideLogoutButton && (
            <button
              className="global-header__btn"
              onClick={() => {
                if (onLogout) {
                  onLogout();
                } else {
                  nav(logoutTo);
                }
              }}
            >
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          )}
          {showLoginButton && (
            <button
              className="global-header__btn global-header__btn--login"
              onClick={() => {
                if (onLogin) {
                  onLogin();
                } else {
                  nav(loginTo);
                }
              }}
            >
              <FaSignInAlt />
              <span>Login</span>
            </button>
          )}
        </div>
      </div>

      {/* Profile Update Popup */}
      {showProfilePopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: profileData.hasCustomerAccess && profileData.hasOrganizerAccess ? '520px' : '440px',
            width: profileData.hasCustomerAccess && profileData.hasOrganizerAccess ? '92%' : '88%',
            height: 'auto',
            maxHeight: '80vh',
            overflow: 'hidden',
            position: 'relative',
            border: '2px solid #EFB034FF',
          }}>
            <div style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              zIndex: 2,
            }}>
              <button
                className="profile-popup-close"
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '18px',
                  color: '#EFB034FF',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => setShowProfilePopup(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{
              overflowY: 'auto',
              maxHeight: '80vh',
              padding: '16px',
              paddingTop: '20px',
              textAlign: 'center',
            }}>
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 8px',
              borderRadius: '50%',
              background: '#EFB034FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontSize: '22px',
            }}>
              <FaUser />
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: 700,
              color: '#EFB034FF',
              margin: '0 0 4px 0',
            }}>
              {profileData.hasCustomerAccess && profileData.hasOrganizerAccess 
                ? 'Account Details' 
                : (isProfileComplete ? 'Profile Information' : 'Update Profile')
              }
            </h3>
            
            <p style={{
              fontSize: '11px',
              color: '#666',
              lineHeight: '1.3',
              margin: '0 0 12px 0',
            }}>
              {profileData.hasCustomerAccess && profileData.hasOrganizerAccess
                ? 'Your complete account information'
                : (isProfileComplete ? 'Your profile information' : 'Please complete your profile information')
              }
            </p>

            {profileData.hasCustomerAccess && profileData.hasOrganizerAccess ? (
              // Show comprehensive account details for users with both access types
              <div style={{ textAlign: 'left', marginBottom: '16px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#f59e0b',
                    margin: '0 0 8px 0',
                    paddingBottom: '4px',
                    borderBottom: '1px solid #e9ecef',
                  }}>
                    Personal Information
                  </h4>
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Full Name:</span>
                    <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>
                      {profileData.firstname} {profileData.surname}
                    </span>
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Email:</span>
                    <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>
                      {profileData.email}
                    </span>
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Phone:</span>
                    <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>
                      {profileData.cellnumber}
                    </span>
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Role:</span>
                    <span style={{ marginLeft: '8px', color: '#666', textTransform: 'capitalize', fontSize: '13px' }}>
                      {profileData.role}
                    </span>
                  </div>
                </div>

                {(profileData.company || profileData.vat_no) && profileData.hasOrganizerAccess && (
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#f59e0b',
                    margin: '0 0 8px 0',
                    paddingBottom: '4px',
                    borderBottom: '1px solid #fde68a',
                  }}>
                    Business Information
                  </h4>
                  {profileData.company && (
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Company:</span>
                      <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>
                        {profileData.company}
                      </span>
                    </div>
                  )}
                  {profileData.vat_no && (
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>VAT Number:</span>
                      <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>
                        {profileData.vat_no}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {profileData.hasOrganizerAccess && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', paddingBottom: '4px', borderBottom: '1px solid #fde68a' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#f59e0b', margin: 0 }}>
                      Banking Information
                    </h4>
                    {!isEditingBanking && (
                      <button
                        onClick={startEditBanking}
                        style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 6px', borderRadius: '4px' }}
                      >
                        <FaEdit size={12} /> Edit
                      </button>
                    )}
                  </div>

                  {isEditingBanking ? (
                    <div>
                      {/* Bank dropdown */}
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#333', marginBottom: '3px' }}>Bank</label>
                        <select
                          value={bankingForm.bank_name}
                          onChange={handleBankSelect}
                          style={{ width: '100%', padding: '7px 10px', border: `1.5px solid ${bankingErrors.bank_name ? '#ef4444' : '#ddd'}`, borderRadius: '6px', fontSize: '13px', background: '#f9f9f9', boxSizing: 'border-box' }}
                        >
                          <option value="">Select a bank…</option>
                          {bankList.map(b => <option key={b.code} value={b.name}>{b.name}</option>)}
                        </select>
                        {bankingErrors.bank_name && <span style={{ fontSize: '11px', color: '#ef4444' }}>{bankingErrors.bank_name}</span>}
                      </div>

                      {/* Account name */}
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#333', marginBottom: '3px' }}>Account Name</label>
                        <input
                          type="text"
                          value={bankingForm.bank_account_name}
                          onChange={e => { setBankingForm(f => ({ ...f, bank_account_name: e.target.value })); setBankingErrors(p => ({ ...p, bank_account_name: '' })); }}
                          style={{ width: '100%', padding: '7px 10px', border: `1.5px solid ${bankingErrors.bank_account_name ? '#ef4444' : '#ddd'}`, borderRadius: '6px', fontSize: '13px', background: '#f9f9f9', boxSizing: 'border-box' }}
                          placeholder="Account holder name"
                        />
                        {bankingErrors.bank_account_name && <span style={{ fontSize: '11px', color: '#ef4444' }}>{bankingErrors.bank_account_name}</span>}
                      </div>

                      {/* Account number */}
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#333', marginBottom: '3px' }}>Account Number</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={bankingForm.bank_account_number}
                          onChange={e => { setBankingForm(f => ({ ...f, bank_account_number: e.target.value.replace(/\D/g, '') })); setBankingErrors(p => ({ ...p, bank_account_number: '' })); }}
                          style={{ width: '100%', padding: '7px 10px', border: `1.5px solid ${bankingErrors.bank_account_number ? '#ef4444' : '#ddd'}`, borderRadius: '6px', fontSize: '13px', background: '#f9f9f9', boxSizing: 'border-box' }}
                          placeholder="Digits only"
                        />
                        {bankingErrors.bank_account_number && <span style={{ fontSize: '11px', color: '#ef4444' }}>{bankingErrors.bank_account_number}</span>}
                      </div>

                      {bankingMessage && (
                        <div style={{ fontSize: '12px', padding: '7px 10px', borderRadius: '5px', marginBottom: '8px', background: bankingMessage.includes('success') ? '#d4edda' : '#f8d7da', color: bankingMessage.includes('success') ? '#155724' : '#721c24', border: `1px solid ${bankingMessage.includes('success') ? '#c3e6cb' : '#f5c6cb'}` }}>
                          {bankingMessage}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => { setIsEditingBanking(false); setBankingErrors({}); setBankingMessage(''); }}
                          disabled={bankingLoading}
                          style={{ flex: 1, padding: '7px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveBanking}
                          disabled={bankingLoading}
                          style={{ flex: 1, padding: '7px', background: '#F4B22C', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                        >
                          <FaSave size={11} />
                          {bankingLoading ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {profileData.bank_name && (
                        <div style={{ marginBottom: '6px' }}>
                          <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Bank:</span>
                          <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>{profileData.bank_name}</span>
                        </div>
                      )}
                      {profileData.bank_account_name && (
                        <div style={{ marginBottom: '6px' }}>
                          <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Account Name:</span>
                          <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>{profileData.bank_account_name}</span>
                        </div>
                      )}
                      {profileData.bank_account_number && (
                        <div style={{ marginBottom: '6px' }}>
                          <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Account Number:</span>
                          <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>{'****' + profileData.bank_account_number.slice(-4)}</span>
                        </div>
                      )}
                      {!profileData.bank_name && !profileData.bank_account_name && !profileData.bank_account_number && (
                        <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>No banking details on file. Click Edit to add them.</p>
                      )}
                    </>
                  )}
                </div>
              )}

                <div>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#f59e0b',
                    margin: '0 0 8px 0',
                    paddingBottom: '4px',
                    borderBottom: '1px solid #fde68a',
                  }}>
                    Access Levels
                  </h4>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: profileData.hasCustomerAccess ? '#28a745' : '#dc3545'
                      }}></div>
                      <span style={{ color: '#666', fontSize: '12px' }}>Customer Access</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: profileData.hasOrganizerAccess ? '#28a745' : '#dc3545'
                      }}></div>
                      <span style={{ color: '#666', fontSize: '12px' }}>Organizer Access</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Show existing form for single access type users
              <div style={{ textAlign: 'left', marginBottom: '16px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#333',
                    marginBottom: '4px',
                  }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={profileData.firstname}
                    onChange={(e) => setProfileData({...profileData, firstname: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#fbbf24'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    placeholder="Enter your first name"
                    disabled={isProfileComplete}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#333',
                    marginBottom: '4px',
                  }}>
                    Surname
                  </label>
                  <input
                    type="text"
                    value={profileData.surname}
                    onChange={(e) => setProfileData({...profileData, surname: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#fbbf24'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    placeholder="Enter your surname"
                    disabled={isProfileComplete}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#333',
                    marginBottom: '4px',
                  }}>
                    Cell Number
                  </label>
                  <input
                    type="tel"
                    value={profileData.cellnumber}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setProfileData({...profileData, cellnumber: digitsOnly});
                      if (validationErrors.cellnumber) {
                        setValidationErrors(prev => ({ ...prev, cellnumber: '' }));
                      }
                    }}
                    maxLength={10}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: validationErrors.cellnumber ? '2px solid #ef4444' : '2px solid #e0e0e0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#fbbf24'}
                    onBlur={(e) => {
                      e.target.style.borderColor = validationErrors.cellnumber ? '#ef4444' : '#e0e0e0';
                    }}
                    placeholder="Enter your cell number"
                    disabled={isProfileComplete}
                  />
                  {validationErrors.cellnumber && (
                    <span style={{
                      display: 'block',
                      marginTop: '4px',
                      fontSize: '11px',
                      color: '#ef4444',
                    }}>
                      {validationErrors.cellnumber}
                    </span>
                  )}
                  <span style={{
                    display: 'block',
                    marginTop: '2px',
                    fontSize: '11px',
                    color: profileData.cellnumber.length === 10 ? '#22c55e' : '#9ca3af',
                  }}>
                    {profileData.cellnumber.length}/10 digits
                  </span>
                </div>
              </div>
            )}

            {updateMessage && (
              <div style={{
                padding: '6px 10px',
                borderRadius: '4px',
                fontSize: '12px',
                marginBottom: '12px',
                backgroundColor: updateMessage.includes('success') ? '#d4edda' : '#f8d7da',
                color: updateMessage.includes('success') ? '#155724' : '#721c24',
                border: `1px solid ${updateMessage.includes('success') ? '#c3e6cb' : '#f5c6cb'}`,
              }}>
                {updateMessage}
              </div>
            )}

          {/* Buttons - Conditional based on profile completeness and access levels */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              {(profileData.hasCustomerAccess && profileData.hasOrganizerAccess) || isProfileComplete ? (
                // Only show close button for complete profiles or dual access users
                <button
                  style={{
                    background: '#EFB034FF',
                    color: '#ffffff',
                    border: '1px solid #EFB034FF',
                    padding: '8px 20px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background 0.2s ease, color 0.2s ease',
                    boxShadow: 'none',
                  }}
                  onClick={() => setShowProfilePopup(false)}
                >
                  Close
                </button>
              ) : (
                // Show cancel and save buttons for incomplete profiles
                <>
                  <button
                    style={{
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '8px 20px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onClick={() => setShowProfilePopup(false)}
                  >
                    Cancel
                  </button>

                  <button
                    style={{
                      background: '#EFB034FF',
                      color: '#ffffff',
                      border: '1px solid #EFB034FF',
                      padding: '8px 20px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background 0.2s ease, color 0.2s ease',
                      boxShadow: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                    onClick={handleProfileUpdate}
                    disabled={isUpdating}
                  >
                    <FaSave />
                    {isUpdating ? 'Updating...' : 'Save Profile'}
                  </button>
                </>
              )}
            </div>
            </div>
          </div>
        </div>
      )}
    </header>

    <footer className="global-footer">
      <div className="global-footer__content">
        <p>&copy; 2026 SmartBuilders (Pty) Ltd. All rights reserved.</p>
      </div>
    </footer>
    </>
  );
};

export default GlobalHeader;
